import React, { useMemo, useId } from 'react';
import { User } from '../../../../shared/types';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import { game as gameConfig } from '../../../../config';
interface StatsSectionProps {
  currentUser: User;
}

function KpiTile({
  icon,
  label,
  value,
  sub,
  accent,
}: {
  icon: string;
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent: 'emerald' | 'amber' | 'sky' | 'violet' | 'slate' | 'rose';
}) {
  const border =
    accent === 'emerald'
      ? 'border-emerald-500/30 from-emerald-950/30'
      : accent === 'amber'
        ? 'border-amber-500/30 from-amber-950/25'
        : accent === 'sky'
          ? 'border-sky-500/30 from-sky-950/25'
          : accent === 'violet'
            ? 'border-violet-500/30 from-violet-950/25'
            : accent === 'rose'
              ? 'border-rose-500/30 from-rose-950/25'
              : 'border-slate-600/40 from-slate-900/80';
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-gradient-to-br ${border} to-slate-950/90 p-3 shadow-lg shadow-black/20 sm:p-4`}
    >
      <div className="pointer-events-none absolute -right-4 -top-4 h-16 w-16 rounded-full bg-white/5" />
      <div className="flex items-start justify-between gap-2">
        <span className="text-2xl opacity-90 sm:text-3xl" aria-hidden>
          {icon}
        </span>
      </div>
      <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-1 text-xl font-bold tabular-nums tracking-tight text-white sm:text-2xl">{value}</p>
      {sub ? <p className="mt-1 text-[11px] leading-snug text-slate-400">{sub}</p> : null}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-300">{children}</h3>
      <div className="h-px flex-1 bg-gradient-to-r from-emerald-500/40 to-transparent" />
    </div>
  );
}

const StatsSection: React.FC<StatsSectionProps> = ({ currentUser }) => {
  const { t, currentLang } = usePageTranslation('game', 'StatsSection');
  const gradId = `xpGrad-${useId().replace(/:/g, '')}`;
  const locale = currentLang === 'en' ? 'en-US' : 'fr-FR';

  const xpForNextLevel = gameConfig.interventions?.levelUpPerInterventions ?? 10;
  const currentXp = currentUser.xp ?? 0;
  const xpInCurrentLevel = currentXp % xpForNextLevel;
  const xpPct = Math.min(100, (xpInCurrentLevel / xpForNextLevel) * 100);
  const niveau = currentUser.niveau || 1;

  const stats = currentUser.statistiques;
  const interventionsCount = stats?.interventions ?? 0;
  const ticketsCount = stats?.tickets_support ?? 0;
  const memberSince = useMemo(() => {
    if (!currentUser.created_at) return t('na');
    try {
      return new Date(currentUser.created_at).toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return t('na');
    }
  }, [currentUser.created_at, locale, t]);

  const lastLoginLabel = useMemo(() => {
    const raw = currentUser.last_login || currentUser.last_activity;
    if (!raw) return t('never');
    try {
      return new Date(raw).toLocaleString(locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return t('na');
    }
  }, [currentUser.last_login, currentUser.last_activity, locale, t]);

  const ringCirc = 2 * Math.PI * 34;
  const ringDash = (xpPct / 100) * ringCirc;
  return (
    <div className="space-y-6 pb-4">
      <SectionTitle>{t('sections.kpi')}</SectionTitle>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
        <KpiTile icon="🏢" label={t('buildings')} value={currentUser.batiments ?? 0} accent="violet" />
        <KpiTile icon="🚒" label={t('vehicles')} value={currentUser.vehicules ?? 0} accent="rose" />
        <KpiTile icon="👥" label={t('personnel')} value={currentUser.statistiques?.personnel ?? 0} accent="slate" />
        <KpiTile icon="🚨" label={t('interventions.total')} value={interventionsCount} accent="emerald" />
        <KpiTile icon="📋" label={t('activity.tickets')} value={ticketsCount} accent="sky" />
      </div>

      <SectionTitle>{t('sections.progression')}</SectionTitle>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex items-center gap-5 rounded-2xl border border-slate-600/45 bg-gradient-to-br from-slate-900/95 to-slate-950 p-5 shadow-xl shadow-black/25">
          <div className="relative h-24 w-24 shrink-0">
            <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90 drop-shadow-lg" aria-hidden>
              <circle cx="40" cy="40" r="34" stroke="rgb(30 41 59)" strokeWidth="8" fill="none" />
              <circle
                cx="40"
                cy="40"
                r="34"
                stroke={`url(#${gradId})`}
                strokeWidth="8"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${ringDash} ${ringCirc}`}
              />
              <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#34d399" />
                  <stop offset="100%" stopColor="#2dd4bf" />
                </linearGradient>
              </defs>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-black text-white">{niveau}</span>
              <span className="text-[10px] font-bold uppercase text-slate-400">{t('level')}</span>
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-white">{t('overview.progress')}</p>
            <p className="mt-1 text-sm text-slate-400">
              {xpInCurrentLevel} / {xpForNextLevel} {t('levelXpUnit')} · {t('kpi.totalXp')} {currentXp}
            </p>
            <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-slate-800">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-teal-400 to-cyan-400 transition-all duration-700"
                style={{ width: `${xpPct}%` }}
              />
            </div>
          </div>
        </div>

        <div
          className={`rounded-2xl border p-5 shadow-xl ${
            currentUser.premium
              ? 'border-amber-400/50 bg-gradient-to-br from-amber-950/50 via-slate-900/80 to-slate-950'
              : 'border-slate-600/45 bg-slate-900/70'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-2xl">{currentUser.premium ? '✨' : '○'}</span>
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">{t('premium.title')}</p>
          </div>
          <p className={`mt-3 text-2xl font-bold ${currentUser.premium ? 'text-amber-200' : 'text-slate-500'}`}>
            {currentUser.premium ? t('premium.active') : t('premium.inactive')}
          </p>
          {currentUser.premium && (currentUser.premium_end || currentUser.premium_until) && (
            <p className="mt-2 text-sm text-amber-100/90">
              {(() => {
                const expiresAt = new Date(currentUser.premium_end || currentUser.premium_until || '');
                const now = new Date();
                const diff = expiresAt.getTime() - now.getTime();
                if (diff <= 0) return t('premium.expired');
                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                if (days > 0) {
                  return `${days}${t('premium.remainingDays')} ${hours}${t('premium.remainingHours')} ${t('premium.remainingMinutes')}`;
                }
                if (hours > 0) {
                  return `${hours}${t('premium.remainingHours')} ${minutes}${t('premium.remainingMinutes')}`;
                }
                return `${minutes}${t('premium.remainingMinutes')}`;
              })()}
            </p>
          )}
        </div>
      </div>

      <SectionTitle>{t('sections.activity')}</SectionTitle>
      <div className="grid grid-cols-1 gap-3">
        <KpiTile
          icon="🛟"
          label={t('alliance')}
          value={currentUser.alliance && currentUser.alliance.trim() ? currentUser.alliance : t('allianceNone')}
          accent="amber"
        />
      </div>

      <SectionTitle>{t('sections.account')}</SectionTitle>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-600/45 bg-slate-900/70 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('account.status')}</p>
          <p className={`mt-2 text-lg font-semibold ${currentUser.activated ? 'text-emerald-300' : 'text-amber-300'}`}>
            {currentUser.activated ? t('account.verified') : t('account.pending')}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-600/45 bg-slate-900/70 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('overview.memberSince')}</p>
          <p className="mt-2 text-lg font-semibold text-slate-100">{memberSince}</p>
        </div>
        <div className="rounded-2xl border border-slate-600/45 bg-slate-900/70 p-4">
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('overview.lastLogin')}</p>
          <p className="mt-2 text-lg font-semibold text-slate-100">{lastLoginLabel}</p>
        </div>
      </div>
    </div>
  );
};

export default StatsSection;
